import { createSelector } from "@reduxjs/toolkit";

import {
  assert,
  designerNodeDataSelector,
  getDesignerNodeDataByIdSelector,
  nodeDesignsByIdSelector,
  nodeIdParamSelector,
  rootNodeDesignerNodeDataOrNullSelector,
} from "@skilitics/nautilus-core-designer-data";

import type { DesignerNode } from "../../types/index.ts";
import { buildDesignerNodeCreate, type BuildDesignerNode } from "../buildDesignerNode.ts";
import { getNodeTitleCreate } from "../getNodeTitle.ts";
import { getLayoutCustomTitleForContentNodeSelector } from "../layoutSelectors/index.ts";
import { useNautilusSelector } from "../shared.ts";
import { getManifestSelector } from "./appConfigSelectors.ts";
import { monolingualizeConfigSelector } from "./nodeConfigSelectors.ts";
import { getParentContextByIdSelector } from "./parentContextSelectors.ts";
import { getNodeProfileByIdSelector } from "./profileSelectors.ts";

// to break circular dependency
export const getNodeTitleCreateArgsSelector = createSelector(
  [getManifestSelector, getParentContextByIdSelector, getLayoutCustomTitleForContentNodeSelector],
  (getManifest, getParentContext, getLayoutTitle) => ({
    getManifest,
    getParentContext,
    getLayoutTitle,
  }),
);

export const buildDesignerNodeSelector = createSelector(
  [getNodeTitleCreateArgsSelector, monolingualizeConfigSelector, getNodeProfileByIdSelector],
  (nodeTitleCreateArgs, monolingualizeConfig, getNodeProfileById): BuildDesignerNode =>
    // buildDesignerNode now uses global WeakMap cache internally,
    // so we don't need to wrap it in memoize() here
    buildDesignerNodeCreate({
      getNodeTitle: getNodeTitleCreate(nodeTitleCreateArgs),
      monolingualizeConfig,
      getNodeProfileById,
    }),
);

export const rootDesignerNodeOrNullSelector = createSelector(
  [buildDesignerNodeSelector, rootNodeDesignerNodeDataOrNullSelector],
  (buildDesignerNode, node): DesignerNode | null => (node ? buildDesignerNode(node) : null),
);

export const getDesignerNodeByIdSelector = createSelector(
  [buildDesignerNodeSelector, getDesignerNodeDataByIdSelector],
  (buildDesignerNode, getDesignerNodeData) => (nodeId: number) => buildDesignerNode(getDesignerNodeData(nodeId)),
);

export const designerNodeSelector = createSelector(
  [buildDesignerNodeSelector, designerNodeDataSelector],
  (buildDesignerNode, nodeData) => buildDesignerNode(nodeData),
);

/**
 * Null-safe by-id node resolution for framework components holding only a node
 * reference — a deleted node can still render briefly before unmounting.
 */
export const designerNodeOrNullSelector = createSelector(
  [buildDesignerNodeSelector, getDesignerNodeDataByIdSelector, nodeDesignsByIdSelector, nodeIdParamSelector],
  (buildDesignerNode, getDesignerNodeData, designsById, nodeId): DesignerNode | null =>
    nodeId in designsById ? buildDesignerNode(getDesignerNodeData(nodeId)) : null,
);

export const designerNodeTypeIdOrNullSelector = createSelector(
  [nodeDesignsByIdSelector, nodeIdParamSelector],
  (designsById, nodeId) => {
    const { typeId } = designsById[nodeId];
    return typeId ?? null;
  },
);

export const designerNodeTypeIdSelector = createSelector(
  [nodeDesignsByIdSelector, nodeIdParamSelector],
  (designsById, nodeId) => {
    const { typeId } = designsById[nodeId];
    assert(typeId, `No node found with id ${nodeId}`);
    return typeId;
  },
);

// TODO: need an immediate children on the data selector for efficiency
export const designerNodeImmediateChildrenSelector = createSelector(
  [buildDesignerNodeSelector, designerNodeDataSelector],
  (buildDesignerNode, { childNodes: childNodeDatas }) =>
    Object.fromEntries(
      Object.entries(childNodeDatas ?? {}).map(([name, childNodeOrNodes]) => {
        const builtChildNodeOrNodes = Array.isArray(childNodeOrNodes)
          ? childNodeOrNodes.map(buildDesignerNode)
          : childNodeOrNodes
            ? buildDesignerNode(childNodeOrNodes)
            : undefined;
        return [name, builtChildNodeOrNodes];
      }),
    ),
);

/**
 * @deprecated this should be replaced by the node public API — see
 * `useNodeProfileById`. Or querying model.
 */
export const useGetById = () => {
  const getDesignerNodeDataById = useNautilusSelector(getDesignerNodeDataByIdSelector);
  return getDesignerNodeDataById;
};

/**
 * @deprecated this should be replaced by the node public API — see
 * `useNodeProfileById`. Or querying model.
 */
export const useGetDesignerNodeById = () => {
  const getDesignerNodeById = useNautilusSelector(getDesignerNodeByIdSelector);
  return getDesignerNodeById;
};
